<!-- src/components/chest/ChestComponent.vue -->
<template>
  <div class="chest-backdrop">
    <div class="chest-panel">
      <h2 class="chest-title">{{ chestName }}</h2>

      <p v-if="error" class="chest-error">{{ error }}</p>

      <div v-if="loading" class="chest-status">Prising it open…</div>

      <template v-else>
        <!--
          One column, not two. A bargain has two sides that each put something
          on the table; a chest only offers. There is nothing to give back, so
          there is nothing to show on the other side.
        -->
        <div class="chest-contents">
          <ItemHolder
            :items="displayItems"
            :show-header="false"
            :overlay="false"
            :disabled="busy"
            :row-count="3"
            :col-count="4"
            @item-clicked="takeOne"
          />
          <p class="chest-hint">
            {{ items.length
              ? 'Click an item to take it, or take everything at once.'
              : 'The chest is empty.' }}
          </p>
        </div>

        <div class="chest-actions">
          <HudButton
            v-if="items.length"
            :type="ButtonType.SUBMIT"
            :disabled="busy"
            @click="takeAll"
          >
            Take all ({{ items.length }})
          </HudButton>
          <HudButton :type="ButtonType.CANCEL" :disabled="busy" @click="close">
            {{ items.length ? 'Leave the rest' : 'Close' }}
          </HudButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ItemHolder from '@/components/Item/ItemHolder.vue';
import HudButton from '@/components/btn/HudButton.vue';
import { ButtonType } from '@/types/ButtonType';
import chestService from '@/services/chestService';

export default {
  name: 'ChestComponent',
  components: { ItemHolder, HudButton },
  props: {
    chestId: {
      type: String,
      required: true
    },
    /** Name from the map token, so the panel has a title before the fetch lands. */
    chestName: {
      type: String,
      default: 'Chest'
    }
  },
  emits: ['closed', 'looted'],
  data() {
    return {
      // Options API templates only see the instance, so the enum has to live on
      // it — an imported constant is not in scope for the template.
      ButtonType,
      items: [],
      loading: true,
      busy: false,
      error: null
    };
  },
  computed: {
    /**
     * ItemCell reads `itemData.item` and `itemData.charges_left`, but the chest
     * endpoint returns the item flattened — it is a manifest, not an inventory.
     * Reshaping here rather than nesting on the server keeps the API honest
     * about what a chest entry is and still lets the item grid draw it.
     */
    displayItems() {
      return this.items.map(entry => ({
        id: entry.world_item_id,
        charges_left: entry.charges_left,
        item: {
          name: entry.name,
          description: entry.description,
          icon: entry.icon,
          type: (entry.type || '').toLowerCase()
        }
      }));
    }
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      this.error = null;
      try {
        const chest = await chestService.getChest(this.chestId);
        this.items = chest.items || [];
      } catch (error) {
        this.error = this.readError(error, 'Could not open the chest.');
        this.items = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * @param item the grid emits the whole entry, not its id — see ItemHolder.
     *             A bare id is accepted too so the handler survives either.
     */
    async takeOne(item) {
      if (this.busy) return;
      const worldItemId = typeof item === 'string' ? item : item?.id;
      if (!worldItemId) return;

      this.busy = true;
      this.error = null;
      try {
        const chest = await chestService.takeItem(this.chestId, worldItemId);
        this.items = chest.items || [];
        this.$emit('looted', { chestId: this.chestId, emptied: !chest.is_active });
        // An emptied chest leaves the board, so there is nothing left to look at.
        if (!chest.is_active) this.close();
      } catch (error) {
        this.error = this.readError(error, 'Could not take that item.');
      } finally {
        this.busy = false;
      }
    },

    async takeAll() {
      if (this.busy) return;
      this.busy = true;
      this.error = null;
      try {
        const chest = await chestService.takeAll(this.chestId);
        this.items = chest.items || [];
        this.$emit('looted', { chestId: this.chestId, emptied: !chest.is_active });
        this.close();
      } catch (error) {
        this.error = this.readError(error, 'Could not empty the chest.');
        this.busy = false;
      }
    },

    close() {
      this.$emit('closed');
    },

    /** DRF puts a ValidationError's message in a bare list; surface it verbatim. */
    readError(error, fallback) {
      const data = error?.response?.data;
      if (Array.isArray(data) && data.length) return String(data[0]);
      if (typeof data === 'string') return data;
      if (data?.detail) return String(data.detail);
      return fallback;
    }
  }
};
</script>

<style scoped>
.chest-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  background: radial-gradient(ellipse at center, rgba(3, 11, 16, 0.72) 0%, rgba(2, 6, 9, 0.92) 100%);
  backdrop-filter: blur(6px) saturate(0.85);
  -webkit-backdrop-filter: blur(6px) saturate(0.85);
  /* Same layer as the bargain: above the action bar (2000), below the dice
     roller (10000). At 1200 the action holder covered the take buttons. */
  z-index: 9000;
}

/* The frame the dossier, the inventory and the bargain all share. */
.chest-panel {
  position: relative;
  min-width: 26rem;
  max-width: 90vw;
  padding: 1.1rem 1.35rem 1.35rem;
  color: #d3e0e6;
  border: 1px solid rgba(0, 255, 255, 0.18);
  border-radius: 0.375rem;
  background: linear-gradient(160deg, rgba(12, 19, 25, 0.96) 0%, rgba(6, 10, 14, 0.985) 100%);
  box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.72),
      inset 0 1px 0 rgba(62, 233, 240, 0.06);
}

.chest-title {
  margin: 0 0 1rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #f2c489;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 10, 14, 0.95);
}

.chest-status,
.chest-hint {
  margin: 0.7rem 0 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  color: #7f929c;
}

.chest-error {
  margin: 0 0 0.8rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid rgba(232, 138, 132, 0.4);
  border-radius: 0.25rem;
  background: rgba(232, 138, 132, 0.1);
  color: #e88a84;
  font-size: 0.76rem;
  text-align: center;
}

.chest-contents {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chest-actions {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 1.2rem;
}
</style>
